import type { ReactNode } from 'react';

import type { Participant, ParticipantPlayType } from '../../schema/sport/entry.js';
import { cn } from '../utils/entry.js';
import type { NameSize } from './utils/getTeamNameAndTitle.js';
import { ParticipantName } from './utils/ParticipationName.js';

type CouponEventParticipantsProps = {
  className?: string;
  participants?: (Participant | undefined)[];
  eventDescription: string;
  participantType?: ParticipantPlayType;
  children?: ReactNode;
  participantClassName?: string;
  nameSize?: NameSize;
};

/**
 * Component that handles the display of participants in a coupon event.
 *
 * @param param0 Props
 * @param param0.className Additional class names
 * @param param0.participants The participants for the coupon event
 * @param param0.eventDescription The description of the event if participants are not available
 * @param param0.participantType The type of participants in the event, e.g., Team or Player
 * @param param0.children Child components
 * @param param0.participantClassName Additional class names for the participant names
 * @param param0.nameSize The size of the participant names, e.g., full or short
 * @returns JSX component that displays the participants in a coupon event.
 */
export default function CouponEventParticipants({
  className,
  participants,
  eventDescription,
  participantType,
  children,
  participantClassName,
  nameSize,
}: CouponEventParticipantsProps): ReactNode {
  const hasParticipants = participants?.length === 2;
  const [homeParticipant, awayParticipant] = participants ?? [];
  // Descriptions are formatted as "Home - Away", so split them into one line per participant
  const [homeDescription, awayDescription] = eventDescription.split(/\s+-\s+/);

  return (
    <div className={cn('grid gap-y-1 whitespace-nowrap [grid-area:description]', className)}>
      {hasParticipants && (
        <>
          {homeParticipant && (
            <ParticipantName
              className={cn('home-participant', participantClassName)}
              nameSize={nameSize ?? 'full'}
              participant={homeParticipant}
            />
          )}
          {Boolean(children) && children}
          {awayParticipant && (
            <ParticipantName
              className={cn('away-participant', participantClassName)}
              nameSize={nameSize ?? 'full'}
              participant={awayParticipant}
            />
          )}
        </>
      )}
      {/* Always render event description if a participant is missing */}
      {!hasParticipants && eventDescription && (
        <>
          <span className="participant-description" translate="no">
            {homeDescription}
          </span>
          {awayDescription && (
            <span className="participant-description" translate="no">
              {awayDescription}
            </span>
          )}
        </>
      )}
      {participantType === 'Player' && <span className="text-xs sm:text-sm">Vem gör flest mål?</span>}
    </div>
  );
}
